import NotInterestedIcon from "@mui/icons-material/NotInterested";
import {
    CardHeader,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from "@mui/material";
import Button from "@mui/material/Button";
import { withStyles } from "@mui/styles";
import axios from "axios";
import * as React from "react";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

interface EnvironmentState {
    versions: {
        gameVersion: string[];
        authVersion: string[];
        chatVersion: string[];
        simulatorVersion: string[];
        matchmakingVersion: string[];
        writerVersion: string[];
        analyticsVersion: string[];
        schedulerVersion: string[];
        analyticsSchedulerVersion: string[];
        dashboardVersion: string[];
        logicBranch: string[];
    },

    clusters: any[];
    loading: boolean;
    versionsLoaded: boolean;
    modalVisible: boolean;

    createForm: {
        name: string;
        gameVersion: string;
        authVersion: string;
        chatVersion: string;
        simulatorVersion: string;
        matchmakingVersion: string;
        writerVersion: string;
        analyticsVersion: string;
        schedulerVersion: string;
        analyticsSchedulerVersion: string;
        dashboardVersion: string;
        logicBranch: string;
    }
}

interface PlayersProps {
    activeUser: any;
    activeEnv: any;
    classes: any;
}

class EnvironmentsWrapped extends React.Component<PlayersProps, EnvironmentState> {

    constructor(props: PlayersProps) {
        super(props);

        this.state = {
            versions: {
                gameVersion: [],
                authVersion: [],
                chatVersion: [],
                simulatorVersion: [],
                matchmakingVersion: [],
                writerVersion: [],
                analyticsVersion: [],
                schedulerVersion: [],
                analyticsSchedulerVersion: [],
                dashboardVersion: [],
                logicBranch: [],
            },

            clusters: [],
            loading: true,
            versionsLoaded: false,
            modalVisible: false,
            createForm: {
                name: "",
                gameVersion: "stage",
                authVersion: "stage",
                chatVersion: "stage",
                simulatorVersion: "stage",
                matchmakingVersion: "stage",
                writerVersion: "stage",
                analyticsVersion: "stage",
                schedulerVersion: "stage",
                analyticsSchedulerVersion: "stage",
                dashboardVersion: "stage",
                logicBranch: "stage"
            }
        };

        document.title = Helpers.documentTitle("Environments");
    }

    componentDidMount() {
        this.fetch();
    }

    fetch = () => {
        axios.get(`${Config.getInstance.apiUrl}/api/environments`, {
            headers: {
                Authorization: Helpers.getSessionId(),
                env: this.props.activeEnv,
            }
        }).then(response => {
            const data = response.data;

            this.setState({
                loading: false,
                clusters: data || [],
            });
        });

        axios.get(`${Config.getInstance.apiUrl}/api/environments/versions`, {
            headers: {
                Authorization: Helpers.getSessionId(),
                env: this.props.activeEnv,
            }
        }).then(response => {
            const data = response.data;

            this.setState({
                versionsLoaded: true,
                versions: data,
            });
        });
    };

    hideModal = () => {
        this.setState({
            modalVisible: false
        });
    };

    showModal = () => {
        this.setState({
            modalVisible: true,
            createForm: {
                name: "",
                gameVersion: "stage",
                authVersion: "stage",
                chatVersion: "stage",
                simulatorVersion: "stage",
                matchmakingVersion: "stage",
                writerVersion: "stage",
                analyticsVersion: "stage",
                schedulerVersion: "stage",
                analyticsSchedulerVersion: "stage",
                dashboardVersion: "stage",
                logicBranch: "stage"
            }
        });
    };

    onFormChange = (e, key: keyof EnvironmentState["createForm"]) => {
        const form = this.state.createForm;

        form[key] = e.target.value;

        this.setState({
            createForm: form
        });
    }

    createEnv = async () => {
        if (!confirm("Make sure you select the correct versions you need, as the process will take at least 10 minutes")) {
            return;
        }

        try {
            const response = await axios.post(`${Config.getInstance.apiUrl}/api/environments`, this.state.createForm, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                }
            });

            Object.assign(document.createElement("a"), {
                target: "_blank",
                href: response.data.url,
            }).click();

            this.hideModal();
        } catch (e) {
            console.error(e);

            PopupMessage.error("Something went wrong!");
        }
    };

    updateEnv = async (name: string) => {
        if (!confirm("Are you sure you want to do this? This process will take at least 10 minutes")) {
            return;
        }

        try {
            const response = await axios.put(`${Config.getInstance.apiUrl}/api/environments/${name}`, {}, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                }
            });

            Object.assign(document.createElement("a"), {
                target: "_blank",
                href: response.data.url,
            }).click();

            this.hideModal();
        } catch (e) {
            console.error(e);

            PopupMessage.error("Something went wrong!");
        }
    };

    syncEnv = async (name: string) => {
        try {
            await axios.patch(`${Config.getInstance.apiUrl}/api/environments/${name}`, {}, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                }
            });

            PopupMessage.success("Files synced successfully");
        } catch (e) {
            console.error(e);

            PopupMessage.error("Something went wrong!");
        }
    };

    deleteEnv = async (name: string) => {
        if (!confirm("Are you sure you want to do this? This process will take at least 10 minutes")) {
            return;
        }

        try {
            const response = await axios.delete(`${Config.getInstance.apiUrl}/api/environments/${name}`, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                }
            });

            Object.assign(document.createElement("a"), {
                target: "_blank",
                href: response.data.url,
            }).click();

            this.hideModal();
        } catch (e) {
            console.error(e);

            PopupMessage.error("Something went wrong!");
        }
    };

    renderFormInput(key: keyof EnvironmentState["createForm"], label: string) {
        return <FormControl style={{width: "100%", marginTop: "25px"}}>
            <InputLabel>
                {label}
            </InputLabel>
            <Select
                value={this.state.createForm[key]}
                onChange={(e) => this.onFormChange(e, key)}
                style={{width: "100%"}}
                label={label}
                inputProps={{
                    name: key,
                    id: key,
                }}
            >
                {this.state.versions[key].map(version => <MenuItem key={version} value={version}>{version}</MenuItem>)}
            </Select>
        </FormControl>;
    }

    formatTableBody = (items: any[]) => {
        if (this.state.loading) {
            return <TableRow key="loading-row">
                <TableCell colSpan={7}>
                    {Helpers.renderSkeletonRows(5, 50)}
                </TableCell>
            </TableRow>
        }

        if (items.length === 0) {
            return <TableRow key="loading-row">
                <TableCell colSpan={7}>
                    <div className="no-items-placeholder">
                        <NotInterestedIcon/>
                        <h3>No environments found</h3>
                    </div>
                </TableCell>
            </TableRow>;
        }

        const {classes} = this.props;

        return items.map(item => (
            <TableRow
                key={item.id}
                hover={true}
                className={classes.tableRow}
            >
                <TableCell>
                    {item.clusterName.replace("-qa", "")}
                </TableCell>
                <TableCell>
                    {item.runningTasksCount}
                </TableCell>
                <TableCell>
                    {item.pendingTasksCount}
                </TableCell>
                <TableCell>
                    {item.status}
                </TableCell>
                <TableCell>
                    {item.clusterName.replace("-qa", "")}.mavia-services.com
                </TableCell>
                <TableCell>
                    <a href={"http://" + item.clusterName.replace("-qa", "") + ".mavia-services.com:9000"}
                       target="_blank">
                        http://{item.clusterName.replace("-qa", "")}.mavia-services.com:9000
                    </a>
                </TableCell>
                <TableCell>
                    <div className={classes.clusterActions}>
                        <Button
                            disableElevation
                            variant="contained"
                            color="primary"
                            onClick={() => this.updateEnv(item.clusterName.replace("-qa", ""))}
                            style={{ marginRight: 8 }}
                        >
                            Update
                        </Button>

                        <Button
                            disableElevation
                            variant="contained"
                            color="primary"
                            onClick={() => this.syncEnv(item.clusterName.replace("-qa", ""))}
                            style={{ marginRight: 8 }}
                        >
                            Sync Files
                        </Button>

                        <Button
                            disableElevation
                            variant="contained"
                            color="secondary"
                            onClick={() => this.deleteEnv(item.clusterName.replace("-qa", ""))}
                        >
                            Destroy
                        </Button>
                    </div>
                </TableCell>
            </TableRow>
        ));
    };

    pageContent(items) {
        return <React.Fragment>
            <Table className="min-table-size">
                <TableHead>
                    <TableRow>
                        <TableCell style={{width: 150}}>Name</TableCell>
                        <TableCell style={{width: 120}}>Running Tasks</TableCell>
                        <TableCell style={{width: 120}}>Pending Tasks</TableCell>
                        <TableCell style={{width: 120}}>Status</TableCell>
                        <TableCell style={{width: 180}}>Game Address</TableCell>
                        <TableCell style={{width: 180}}>Dashboard</TableCell>
                        <TableCell style={{width: 270}}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {this.formatTableBody(items)}
                </TableBody>
            </Table>
        </React.Fragment>;
    }

    render() {
        const {classes} = this.props;

        return <div className="contentPadding">
            <Paper className="root" elevation={0}>
                <CardHeader
                    title="Environments"
                    action={
                        this.state.versionsLoaded ? <Button disableElevation variant="contained" size="small" onClick={this.showModal}>
                            Add Environment
                        </Button> : <Skeleton width={145} height={45}/>
                    }
                />

                <div style={{float: "left", width: "100%"}}>
                    {this.pageContent(this.state.clusters)}
                </div>

                {this.state.versionsLoaded && <Dialog
                    open={this.state.modalVisible}
                    onClose={this.hideModal}
                >
                    <DialogTitle>
                        Create Environment
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            <InputLabel>
                                <small>
                                    The name must not contain any spaces or special characters (except -) as it
                                    will be used to create the domain name
                                </small>
                            </InputLabel>

                            <FormControl style={{width: "100%"}}>
                                <TextField
                                    required
                                    id="name"
                                    label="Name"
                                    value={this.state.createForm.name}
                                    onChange={(e) => this.onFormChange(e, "name")}
                                    type="text"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    style={{width: "100%"}}
                                    margin="normal"
                                />
                            </FormControl>

                            <InputLabel style={{marginTop: "25px"}}>
                                <small>
                                    Select the correct service versions you need. If you're not sure, ask a developer
                                </small>
                            </InputLabel>

                            {this.renderFormInput("gameVersion", "Game Server Version")}
                            {this.renderFormInput("authVersion", "Auth Server Version")}
                            {this.renderFormInput("chatVersion", "Chat Server Version")}
                            {this.renderFormInput("simulatorVersion", "Battle Simulator Version")}
                            {this.renderFormInput("matchmakingVersion", "Matchmaking Version")}
                            {this.renderFormInput("writerVersion", "Writer Version")}
                            {this.renderFormInput("analyticsVersion", "Analytics Writer Version")}
                            {this.renderFormInput("schedulerVersion", "Scheduler Version")}
                            {this.renderFormInput("analyticsSchedulerVersion", "Analytics Scheduler Version")}
                            {this.renderFormInput("dashboardVersion", "Dashboard Version")}
                            {this.renderFormInput("logicBranch", "Logic Branch")}
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button disableElevation onClick={this.hideModal} color="secondary">
                            Cancel
                        </Button>
                        <Button disableElevation onClick={this.createEnv}
                                disabled={!this.state.createForm.name.trim() || !new RegExp("^[a-z0-9]+(?:-[a-z0-9]+)*$").test(this.state.createForm.name)}
                                color="primary">
                            Create
                        </Button>
                    </DialogActions>
                </Dialog>}
            </Paper>
        </div>;
    }
}

const styles: any = (theme: any) => ({
    root: {
        width: "100%",
        marginTop: 24,
        overflowX: "auto",
    },
    table: {
        minWidth: 700,
    },
    clusterActions: {
        "& > *": {
            margin: 8,
        },
    },
});

export const Environments = withStyles(styles)(EnvironmentsWrapped);
